'use client';

import {
  type StrapiNewsItem,
  useSearchNewsTitles,
  useSearchProfilesProxy,
} from '@outpoll/strapi-client';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import type { FuturesPair } from '@/lib/api/futures/client';
import { useSearchPairs } from '@/lib/api/futures/hooks';
import { useSearchPredictions } from '@/lib/api/search/hooks';
import type { SearchPrediction } from '@/lib/api/search/models';
import { useDebouncedValue } from '@/lib/hooks/useDebouncedValue';
import { matchesPair } from '@/lib/utils/pairFormat';
import { MIN_SEARCH_LENGTH, SEARCH_DEBOUNCE_MS, TAB_LIMIT } from '../core/constants';
import type { SearchCategory } from '../core/types';
import type { TraderRowData } from '../rows/TraderRow';
import { useErrorToast } from './useErrorToast';

export interface SearchResults {
  /** The first answer to this query is still out — the skeleton, not "No results". */
  loading: boolean;
  /** A newer query is on its way while these results stay up. */
  refreshing: boolean;
  counts: Record<SearchCategory, number>;
  pairs: FuturesPair[];
  predictions: SearchPrediction[];
  traders: TraderRowData[];
  news: StrapiNewsItem[];
}

/**
 * Every category's answer to one query, read together so the tabs and the
 * All sections agree. Remote reads wait for the typing to pause; the pairs
 * are filtered here, over the list already loaded. A category whose read
 * fails counts as empty, with one toast for the search.
 *
 * The nickname search knows the volume and the markets traded, not the
 * board place, rank, tasks or trades the design shows — those stay off the
 * row until the service sends them.
 */
export function useSearchResults(query: string): SearchResults {
  const t = useTranslations('search');
  const debounced = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const term = debounced.length >= MIN_SEARCH_LENGTH ? debounced : '';

  const pairsQuery = useSearchPairs();
  const predictionsQuery = useSearchPredictions(term, TAB_LIMIT);
  const profilesQuery = useSearchProfilesProxy(term || null, TAB_LIMIT);
  const newsQuery = useSearchNewsTitles(term || null, TAB_LIMIT);

  useErrorToast(
    predictionsQuery.error ?? profilesQuery.error ?? newsQuery.error ?? null,
    t('errorUnavailable'),
  );

  const pairs = useMemo(
    () => (term ? (pairsQuery.data ?? []).filter((pair) => matchesPair(pair, term)) : []),
    [pairsQuery.data, term],
  );

  const traders = useMemo<TraderRowData[]>(
    () =>
      (profilesQuery.data?.profiles ?? []).map((profile) => ({
        userId: profile.id,
        username: profile.nickname,
        look: profile.look,
        position: null,
        rankIndex: null,
        tasks: null,
        volume: profile.stats?.volumeTraded ?? null,
        activity: profile.stats ? t('markets', { count: profile.stats.marketsTraded }) : null,
      })),
    [profilesQuery.data, t],
  );

  const predictions = predictionsQuery.data ?? [];
  const news = newsQuery.data?.news ?? [];
  const waiting = (q: { isPending: boolean; isError: boolean }) => q.isPending && !q.isError;

  return {
    loading:
      !term ||
      pairsQuery.isLoading ||
      waiting(predictionsQuery) ||
      waiting(profilesQuery) ||
      waiting(newsQuery),
    refreshing:
      Boolean(term) &&
      (debounced !== query.trim() ||
        predictionsQuery.isFetching ||
        profilesQuery.isFetching ||
        newsQuery.isFetching),
    counts: {
      crypto: pairs.length,
      predictions: predictions.length,
      traders: profilesQuery.data?.total ?? traders.length,
      news: newsQuery.data?.total ?? news.length,
    },
    pairs: pairs.slice(0, TAB_LIMIT),
    predictions,
    traders,
    news,
  };
}
